// PROPOSAL PAGE — new credits-based subscription model draft (/newsub).
// Standalone so the live Landing pricing stays untouched. Not wired to payments.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, Minus, Star, Crown, Coins, Users, Eye, Sprout, Gauge, Trophy,
  Video, Brain, Target, Megaphone, ArrowRight, Sparkles,
} from "lucide-react";

const RED = "#C8000F";
const GOLD = "#E6B35A";

const grain = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
};

const Li = ({ children, on = true }) => (
  <li className={`flex gap-2.5 text-sm ${on ? "text-slate-200" : "text-slate-500"}`}>
    {on ? <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: RED }} /> : <Minus className="w-4 h-4 shrink-0 mt-0.5 text-slate-600" />}
    <span>{children}</span>
  </li>
);

const SectionTitle = ({ kicker, title, sub }) => (
  <div className="text-center max-w-2xl mx-auto mb-10">
    {kicker && <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>{kicker}</div>}
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">{title}</h2>
    {sub && <p className="mt-4 text-slate-400">{sub}</p>}
  </div>
);

const SPECTATOR = [
  { name: "Fan", price: "$2.99", unit: "/mo", desc: "Follow up to 3 competitors.", extra: "Additional competitors: 1 credit / competitor / month", popular: false },
  { name: "Plus", price: "$5.99", unit: "/mo", desc: "Follow up to 10 competitors.", extra: "Additional competitors: 1 credit / competitor / month", popular: true },
  { name: "All Access", price: "$9.99", unit: "/mo", desc: "Follow unlimited competitors.", extra: "Built for serious fans who follow the whole season.", popular: false },
];

const CORE = [
  {
    name: "Rookie", icon: Sprout, price: "Free", unit: "", tag: "Start Rodeoing",
    desc: "For casual competitors who want Rodeo's basic tools.",
    accent: "#9AA1AB",
    features: [
      "Find rodeos + dates & schedules", "Entry open & close dates", "View draws already in Rodeo",
      "Watch available draw videos", "Basic competitor results & stats", "Follow 1 competitor",
    ],
    credits: [
      "Prepared entry — 1 credit each", "Draw lookup — 1 credit each", "Results & standings audit — 50 credits",
      "Extra competitor — 1 credit / month",
    ],
  },
  {
    name: "Semi Pro", icon: Gauge, price: "$14.99", unit: "/mo", yearly: "$149/yr", tag: "Run Your Rodeo Season",
    desc: "Spend less time organizing and more time competing.",
    accent: RED, popular: true,
    features: [
      "Everything in Rookie, plus:", "Build your complete rodeo schedule", "Track where you're entered",
      "Official rodeo count tracking", "Entry deadline tracking", "Winnings tracking",
      "Season & performance goals", "Advanced planning tools", "Follow up to 5 competitors",
    ],
    credits: [
      "2 prepared entries / mo (30 / yr)", "2 draw lookups / mo (30 / yr)", "Audit — 30 credits (20 / yr)",
      "Extra entries / lookups — 1 credit each",
    ],
  },
  {
    name: "Professional", icon: Trophy, price: "$29.99", unit: "/mo", yearly: "$299/yr", tag: "Next-Level Rodeo",
    desc: "For serious competitors who want Rodeo working for them all season.",
    accent: GOLD,
    features: [
      "Everything in Semi Pro, plus:", "Unlimited prepared entries", "Unlimited draw lookups",
      "Live, unlimited results & standings audit", "Follow up to 15 competitors", "Automatic performance tracking",
      "Catch % + detailed time breakdowns", "Compare runs over time & vs elite", "Automatic video analysis",
      "Performance trends", "AI Coach + personalized insights",
    ],
    credits: ["No individual audits needed — your season stays audited automatically, all season."],
  },
];

const DEEP = [
  { icon: Video, title: "Performance Tracking", body: "As videos and results enter the system, Rodeo auto-builds your performance history: catch %, average & best times, consistency, trends, misses, penalties and video-linked run data." },
  { icon: Target, title: "Time Difference Comparison", body: "Break a run into segments (e.g. tie-down: Barrier → Throw → String → Legs Crossed → Hands Up) and see exactly where time is gained or lost — run vs run, vs your average, vs your best, vs elite competitors." },
  { icon: Brain, title: "AI Coach", body: "Your performance data powers a personal AI Coach that analyzes videos, times, segments, catch %, consistency, trends and goals against elite benchmarks — and tells you what deserves the most attention." },
];

const COMPARE = [
  ["Starting Price", "$2.99/mo", "Free", "$14.99/mo", "$29.99/mo"],
  ["Find / View Rodeos", "✓", "✓", "✓", "✓"],
  ["Competitors Included", "3–Unlimited", "1", "5", "15"],
  ["Extra Competitor", "1 cr/mo", "1 cr/mo", "1 cr/mo", "1 cr/mo"],
  ["Rodeo Scheduling", "—", "Basic", "✓", "✓"],
  ["Rodeo Count", "—", "—", "✓", "✓"],
  ["Prepared Entries", "—", "1 cr", "2/mo · 30/yr", "Unlimited"],
  ["Draw Lookup", "—", "1 cr", "2/mo · 30/yr", "Unlimited"],
  ["Results Audit", "—", "50 cr", "30 / 20 cr", "Live & Unlimited"],
  ["Winnings Tracking", "—", "—", "✓", "✓"],
  ["Goals", "—", "—", "✓", "✓"],
  ["Performance Tracking", "—", "—", "Basic", "✓"],
  ["Catch Percentage", "—", "—", "—", "✓"],
  ["Time Comparison", "—", "—", "—", "✓"],
  ["Advanced Analytics", "—", "Credits", "Credits", "✓"],
  ["AI Coach", "—", "Credits", "Credits", "✓"],
];

const CREDIT_COSTS = [
  ["Prepared Rodeo Entry", "1 credit"],
  ["Draw Lookup", "1 credit"],
  ["Follow Additional Competitor", "1 credit / month"],
  ["Trade Report", "1 credit"],
  ["Get Counts", "1 credit"],
  ["Results & Standings Audit (Rookie)", "50 credits"],
  ["Audit (Semi Pro monthly / annual)", "30 / 20 credits"],
  ["Audit (Professional)", "Unlimited live"],
];

const Cell = ({ v }) => (
  <td className="px-3 py-2.5 text-center text-sm border-b border-white/5">
    {v === "✓" ? <Check className="w-4 h-4 mx-auto" style={{ color: RED }} /> : v === "—" ? <span className="text-slate-600">—</span> : <span className="text-slate-300">{v}</span>}
  </td>
);

export default function NewSub() {
  const nav = useNavigate();
  const [yearly, setYearly] = useState(false);

  return (
    <div className="min-h-screen bg-[#0B0F16] text-slate-100">
      {/* Draft banner */}
      <div className="sticky top-0 z-30 bg-[#1A1205] border-b border-[#E6B35A]/30 px-4 py-2 flex items-center justify-center gap-2 text-[12px] text-[#E6B35A]" data-testid="newsub-draft-banner">
        <Sparkles className="w-3.5 h-3.5" /> Pricing proposal / draft — not live. The current pricing stays unchanged.
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden px-5 sm:px-8 pt-16 pb-14 text-center" data-testid="newsub-hero">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={grain} />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(200,0,15,0.12)" }} />
        <div className="relative max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ background: "rgba(200,0,15,0.15)", color: "#F26D77" }}>One Rodeo Platform</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5">Choose how you rodeo.</h1>
          <p className="mt-5 text-slate-300 text-base sm:text-lg">Everything you need to follow rodeo, plan your season, manage your entries, and improve your performance — all in one place.</p>
          <p className="mt-2 text-slate-500 text-sm">Follow · Schedule · Enter · Draw · Trade · Track · Audit · Analyze · Improve</p>
        </div>
      </section>

      {/* Spectator */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5" data-testid="newsub-spectator">
        <SectionTitle kicker="Spectator" title="Follow the cowboys & cowgirls you care about"
          sub="You don't have to compete to use Rodeo. Pick your favourites and Rodeo tracks where they're entered, when they're up, draw position, results, standings and available run videos — all in one place." />
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {SPECTATOR.map((s) => (
            <div key={s.name} data-testid={`newsub-spectator-${s.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative rounded-2xl p-6 ${s.popular ? "border-2 bg-[#141A24]" : "border border-white/10 bg-[#121823]"}`}
              style={s.popular ? { borderColor: RED, boxShadow: "0 0 40px rgba(200,0,15,0.22)" } : {}}>
              {s.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={{ background: RED }}><Star className="w-3 h-3" /> Most Popular</span>}
              <div className="flex items-center gap-2 text-slate-400"><Eye className="w-4 h-4" /><span className="text-xs uppercase tracking-wider font-semibold">Spectator</span></div>
              <h3 className="font-display text-2xl font-bold mt-1">{s.name}</h3>
              <div className="mt-3 flex items-end gap-1"><span className="font-display text-4xl font-extrabold">{s.price}</span><span className="text-slate-400 text-sm mb-1.5">{s.unit}</span></div>
              <p className="text-sm text-slate-300 mt-3">{s.desc}</p>
              <p className="text-xs text-slate-500 mt-2">{s.extra}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core tiers */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5" data-testid="newsub-core">
        <SectionTitle kicker="Competitor plans" title="From free tools to a full performance platform"
          sub="Start free and pay only when you need more — or let Rodeo run your whole season." />
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#141A24]">
            <button onClick={() => setYearly(false)} data-testid="newsub-billing-monthly" className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${!yearly ? "text-white" : "text-slate-400"}`} style={!yearly ? { background: RED } : {}}>Monthly</button>
            <button onClick={() => setYearly(true)} data-testid="newsub-billing-yearly" className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${yearly ? "text-white" : "text-slate-400"}`} style={yearly ? { background: RED } : {}}>Yearly</button>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {CORE.map((t) => {
            const Icon = t.icon;
            const showYearly = yearly && t.yearly;
            return (
              <div key={t.name} data-testid={`newsub-tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex flex-col h-full rounded-2xl p-6 ${t.popular ? "border-2 bg-[#141A24]" : "border bg-[#121823]"}`}
                style={{ borderColor: t.popular ? RED : "rgba(255,255,255,0.1)", boxShadow: t.popular ? "0 0 40px rgba(200,0,15,0.22)" : "none" }}>
                {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={{ background: RED }}><Star className="w-3 h-3" /> Most Popular</span>}
                <div className="flex items-center gap-2" style={{ color: t.accent }}><Icon className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-semibold">{t.tag}</span></div>
                <h3 className="font-display text-2xl font-bold mt-1 text-white">{t.name}</h3>
                <p className="text-sm text-slate-400 mt-1 min-h-[40px]">{t.desc}</p>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold">{showYearly ? t.yearly.split("/")[0] : t.price}</span>
                  <span className="text-slate-400 text-sm mb-1.5">{t.price === "Free" ? "" : showYearly ? "/year" : t.unit}</span>
                </div>
                {t.yearly && !yearly && <p className="text-[11px] text-slate-500 mt-1">or {t.yearly}</p>}
                <ul className="mt-5 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className={`flex gap-2.5 text-sm ${f.endsWith("plus:") ? "text-slate-400 text-xs uppercase tracking-wide pt-1" : "text-slate-200"}`}>
                      {!f.endsWith("plus:") && <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: t.accent }} />}
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2"><Coins className="w-3.5 h-3.5" style={{ color: GOLD }} /> Credits & add-ons</div>
                  <ul className="space-y-1.5 text-[12px] text-slate-400">
                    {t.credits.map((c) => <li key={c} className="flex gap-2"><span style={{ color: GOLD }}>•</span>{c}</li>)}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Deep features */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5 bg-[#0E131C]" data-testid="newsub-deep">
        <SectionTitle kicker="Professional" title="Where Rodeo becomes a performance platform"
          sub="The features worth charging for — video analysis, segment timing and AI coaching built on your own run data." />
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {DEEP.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.title} className="rounded-2xl border border-white/10 bg-[#121823] p-6">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(230,179,90,0.12)" }}><Icon className="w-5 h-5" style={{ color: GOLD }} /></div>
                <h3 className="font-display text-xl font-bold text-white">{d.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5" data-testid="newsub-compare">
        <SectionTitle kicker="Compare" title="Every plan, side by side" />
        <div className="max-w-5xl mx-auto overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-[#141A24]">
                <th className="px-3 py-3 text-left text-xs uppercase tracking-wide text-slate-400 font-semibold">Feature</th>
                {["Spectator", "Rookie", "Semi Pro", "Professional"].map((h) => (
                  <th key={h} className="px-3 py-3 text-center text-xs uppercase tracking-wide text-slate-200 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row) => (
                <tr key={row[0]} className="odd:bg-white/[0.02]">
                  <td className="px-3 py-2.5 text-sm text-slate-300 border-b border-white/5 font-medium">{row[0]}</td>
                  {row.slice(1).map((v, i) => <Cell key={i} v={v} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Credits */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5 bg-[#0E131C]" data-testid="newsub-credits">
        <SectionTitle kicker="Rodeo Credits" title="One simple currency across Rodeo" sub="Target value: 1 credit ≈ $1. Buy individual services without changing your subscription." />
        <div className="max-w-2xl mx-auto rounded-2xl border border-white/10 bg-[#121823] divide-y divide-white/5">
          {CREDIT_COSTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-slate-300">{k}</span>
              <span className="text-sm font-semibold" style={{ color: GOLD }}>{v}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-4 max-w-2xl mx-auto">Future AI, video analysis and advanced competitor reports will get credit pricing once processing costs and customer value are established.</p>
      </section>

      {/* Competitor following */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5" data-testid="newsub-following">
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-[#121823] p-7">
          <div className="flex items-center gap-2 text-slate-400 mb-3"><Users className="w-5 h-5" style={{ color: RED }} /><span className="text-xs uppercase tracking-wider font-semibold">Competitor following</span></div>
          <h3 className="font-display text-2xl font-bold text-white">Basic profiles are free. Following is different.</h3>
          <p className="text-sm text-slate-400 mt-2">When you follow a competitor, Rodeo actively tracks them for you: entry info, upcoming rodeos, when they're up, draw, results, standings, available videos and notifications.</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: "rgba(230,179,90,0.12)", color: GOLD }}><Coins className="w-4 h-4" /> 1 competitor = 1 credit / month</div>
        </div>
      </section>

      {/* Sponsorship */}
      <section className="px-5 sm:px-8 py-14 border-t border-white/5 bg-[#0E131C]" data-testid="newsub-sponsorship">
        <SectionTitle kicker="Sponsorship & advertising" title="Monetize the audience, not just the subscription"
          sub="Sponsor inventory across both competitor and spectator audiences — pages, draws, videos, profiles, results, leaderboards, stats, notifications, social and weekly recaps." />
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {["Fastest Runs of the Week — Presented by [Sponsor]", "Tie-Down Roping Leaderboard — Presented by [Sponsor]", "Today's Draw — Presented by [Sponsor]", "Performance Report — Presented by [Sponsor]"].map((e) => (
            <div key={e} className="rounded-xl border border-white/10 bg-[#121823] px-4 py-3 text-sm text-slate-300 flex items-center gap-2"><Megaphone className="w-4 h-4 shrink-0" style={{ color: RED }} />{e}</div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5 text-center" data-testid="newsub-footer">
        <h2 className="font-display text-3xl font-bold text-white">One Rodeo platform</h2>
        <p className="mt-3 text-slate-400 max-w-xl mx-auto">Whether you're watching your favourite cowboy or chasing a championship — Rodeo keeps you connected to what matters.</p>
        <button onClick={() => nav("/")} data-testid="newsub-back-home" className="mt-7 inline-flex items-center gap-2 font-bold py-3 px-6 rounded-xl text-white" style={{ background: RED }}>
          <Crown className="w-4 h-4" /> Back to Rodeo Pilot <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    </div>
  );
}
